import React, { useState, useEffect } from "react";
import {
  Container,
  Table,
  Modal,
  Button,
  Form,
  Spinner,
  Badge,
  Row,
  Col,
  Image,
  Alert,
  Card,
  Pagination,
  Dropdown,
  InputGroup,
  ButtonGroup,
} from "react-bootstrap";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { Search, X } from "lucide-react";
import * as XLSX from "xlsx";
import "./EmployeeDetail.css";
import { showMyAlert } from "../../Utils/alertService";
import { doAuditLog } from "../../Utils/saveHistoryAction";

const EmployeeDetail = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [registrationData, setRegistrationData] = useState([]);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [currentCertificate, setCurrentCertificate] = useState(null);
  const [currentCertificateName, setCurrentCertificateName] = useState("");
  const [showCertificateListModal, setShowCertificateListModal] =
    useState(false);
  const [fromCertificateList, setFromCertificateList] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [certificateFiles, setCertificateFiles] = useState([]);
  const [certificateNames, setCertificateNames] = useState([]);
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [showFilters, setShowFilters] = useState(false)

  const companyKey = sessionStorage.getItem("companyKey");

  let navigate = useNavigate();

  useEffect(() => {
    const checkAuth = () => {
      try {
        const userMobile = sessionStorage.getItem("userMobile");
        if (!userMobile || userMobile.trim() === "") {
          navigate(`/`, {
            replace: true,
          });
        }
      } catch (error) {
        navigate(`/`, { replace: true });
      }
    };
    checkAuth();
  }, [navigate]);

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const previewUrl = URL.createObjectURL(file);
      setEditingEmployee((prev) => ({
        ...prev,
        photographPreview: previewUrl,
      }));
    }
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSignatureFile(file);
      const previewUrl = URL.createObjectURL(file);
      setEditingEmployee((prev) => ({ ...prev, signaturePreview: previewUrl }));
    }
  };

  const handleAddCertificate = () => {
    if (certificateFiles.length < 5) {
      setCertificateFiles([...certificateFiles, null]);
      setCertificateNames([...certificateNames, ""]);
    }
  };

  const handleRemoveCertificate = (index) => {
    const newFiles = certificateFiles.filter((_, i) => i !== index);
    const newNames = certificateNames.filter((_, i) => i !== index);
    setCertificateFiles(newFiles);
    setCertificateNames(newNames);
  };

  const handleCertificateFileChange = (index, file) => {
    const newFiles = [...certificateFiles];
    newFiles[index] = file;
    setCertificateFiles(newFiles);
  };

  const handleCertificateNameChange = (index, name) => {
    const newNames = [...certificateNames];
    newNames[index] = name;
    setCertificateNames(newNames);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredEmployees.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  useEffect(() => {
    const fetchRegistrationData = async () => {
      try {
        const response = await axios.get(
          "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers"
        );
        const filteredData = response.data.filter(
          (d) => d.companyKey === companyKey
        );
        setRegistrationData(filteredData);
      } catch (err) {
        console.error("Error fetching registration data:", err);
      }
    };

    fetchRegistrationData();
  }, []);

  const getDateOfJoining = (employeeId) => {
    if (!registrationData || registrationData.length === 0) return null;

    const employee = registrationData.find(
      (emp) => emp.employee_id === employeeId
    );

    return employee ? employee.approval_date : null;
  };

  const parseCertificates = (certString) => {
    if (!certString || certString === "null" || certString === "undefined")
      return [];

    try {
      let parsed = JSON.parse(certString);
      while (typeof parsed === "string") {
        parsed = JSON.parse(parsed);
      }
      if (Array.isArray(parsed)) {
        return parsed
          .filter((item) => item != null)
          .map((item, index) => {
            if (typeof item === "string") {
              return {
                name: item.trim(),
                fileName: null,
              };
            }
            if (item && typeof item === "object") {
              return {
                name: (item.name || `Certificate ${index + 1}`).trim(),
                fileName: item.fileName || null,
              };
            }
            return {
              name: `Certificate ${index + 1}`,
              fileName: null,
            };
          });
      }

      if (typeof parsed === "object" && parsed !== null) {
        return Object.entries(parsed)
          .filter(([name, fileName]) => name != null)
          .map(([name, fileName]) => ({
            name: name.trim(),
            fileName,
          }));
      }

      return [];
    } catch (e) {
      console.error("Error parsing certificates:", e, certString);
      return [];
    }
  };


  const handleDeleteClick = (employee) => {
    setEmployeeToDelete(employee);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsLoading(true);
      await axios.delete(
        `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollEmployeeBasicDetails_PayrollEmployeeDetails/${employeeToDelete.id}`
      );

      // Log the deletion action
      doAuditLog(
        "Delete",
        "Employee Deleted",
        employeeToDelete,
        {}
      );

      // Show success message
      showMyAlert({
        type: "success",
        title: "Success",
        message: "Employee deleted successfully",
        duration: 2500,
      });

      // Refresh the employee list
      await fetchEmployees();

      // Close modals
      setShowDeleteModal(false);
      setShowModal(false);
      setEmployeeToDelete(null);
    } catch (error) {
      console.error("Error deleting employee:", error);
      showMyAlert({
        type: "danger",
        title: "Delete Failed",
        message: "Failed to delete employee",
        duration: 2500,
      });
      setIsLoading(false);
    }
  };

  const handleDownloadCertificate = async (
    fileUrl,
    filename,
    employeeId,
    certificateName
  ) => {
    try {
      const response = await axios.get(fileUrl, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      link.remove();
    } catch (error) {
      console.error("Error downloading file:", error);
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Danger",
        message: "Unable To Download File",
        duration: 2500, // auto-close in 2.5s
      });
    }
  };

  const handleViewCertificate = (certificateUrl, certificateName) => {
    setCurrentCertificate(certificateUrl);
    setCurrentCertificateName(certificateName);
    setFromCertificateList(true);
    setShowCertificateModal(true);
  };

  const handleViewCertificatesFromTable = (employee) => {
    setSelectedEmployee(employee);
    setShowCertificateListModal(true);
  };

  const getCertificateCount = (employee) => {
    return parseCertificates(employee.certifications).length;
  };

  // Handle items per page change
  const handleItemsPerPageChange = (count) => {
    setItemsPerPage(count);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const API_URL =
    "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollEmployeeBasicDetails_PayrollEmployeeDetails";

  const fetchEmployees = async () => {
    try {
      const { data } = await axios.get(API_URL);
      const filteredData = data.filter((d) => d.companyKey === companyKey);
      const approvedData = filteredData.filter(
        (emp) => emp.approvalStatus === "true"
      );
      const reversedData = [...approvedData].reverse();
      setEmployees(reversedData);
      setFilteredEmployees(reversedData);
      setIsLoading(false);
      return reversedData;
    } catch (err) {
      setError("Failed to fetch employee data");
      setIsLoading(false);
      console.error("Error fetching employees:", err);
      return [];
    }
  };

  useEffect(() => {
    if (!searchTerm && !startDateFilter && !endDateFilter) {
      setFilteredEmployees(employees);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = employees.filter((emp) => {
        const searchMatch =
          !searchTerm ||
          Object.values(emp).some(
            (v) => v && v.toString().toLowerCase().includes(term)
          );

        let dateMatch = true;
        if (startDateFilter || endDateFilter) {
          if (emp.approvedDate && emp.approvedDate !== "null") {
            try {
              const [day, month, year] = emp.approvedDate.split("-");
              const empDate = new Date(
                parseInt(year),
                parseInt(month) - 1,
                parseInt(day)
              );

              if (startDateFilter) {
                const startDate = new Date(startDateFilter);
                startDate.setHours(0, 0, 0, 0);
                dateMatch = dateMatch && empDate >= startDate;
              }

              if (endDateFilter) {
                const endDate = new Date(endDateFilter);
                endDate.setHours(23, 59, 59, 999);
                dateMatch = dateMatch && empDate <= endDate;
              }
            } catch (error) {
              console.error("Error parsing date:", emp.approvedDate, error);
              dateMatch = false;
            }
          } else {
            dateMatch = false;
          }
        }

        return searchMatch && dateMatch;
      });

      setFilteredEmployees(filtered);
      setCurrentPage(1);
    }
  }, [searchTerm, employees, startDateFilter, endDateFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, startDateFilter, endDateFilter]);

  const handleViewDetails = (employee) => {
    setSelectedEmployee(employee);
    setEditingEmployee({ ...employee });
    setIsEditing(false);

    const certs = parseCertificates(employee.certifications);
    setCertificateFiles(new Array(certs.length).fill(null));
    setCertificateNames(certs.map((c) => c?.name || ""));

    setShowModal(true);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingEmployee({ ...selectedEmployee });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditingEmployee((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      const formData = new FormData();
      Object.entries(editingEmployee).forEach(([key, value]) => {
        if (
          value !== undefined &&
          value !== null &&
          !key.startsWith("certificate_") &&
          key !== "certifications"
        ) {
          formData.append(key, value);
        }
      });

      if (photoFile) formData.append("photograph", photoFile);
      if (signatureFile) formData.append("signature", signatureFile);

      const certArray = certificateNames.map((name) => name.trim());

      formData.append("certifications", JSON.stringify(certArray));

      certificateFiles.forEach((file, index) => {
        if (file) {
          formData.append(`certificate_${index}`, file);
        }
      });

      const response = await axios.put(
        `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollEmployeeBasicDetails_PayrollEmployeeDetails/${editingEmployee.id}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      const formDataObj = Object.fromEntries(formData.entries());
      doAuditLog(
        "Update",
        "Employees Detail Approved",
        editingEmployee,
        formDataObj
      );

      setIsEditing(false);
      setIsSaving(false);
      const latest = await fetchEmployees();
      const fresh = latest.find((e) => e.id === response.data.id);

      setSelectedEmployee(fresh);
      setPhotoFile(null);
      setSignatureFile(null);
      setCertificateFiles([]);
    } catch (error) {
      console.error("Error updating employee:", error);
      setIsSaving(false);
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Update Failed",
        message: "Failed to update employee details",
        duration: 2500, // auto-close in 2.5s
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "null") return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const isMobile = window.innerWidth < 768;

  if (isLoading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  const displayValue = (value) => {
    if (value === null || value === "null" || value === "") {
      return "N/A";
    }
    return value;
  };

  const handleDownloadExcel = () => {
    if (filteredEmployees.length === 0) {
      showMyAlert({
        type: "warning", // "success" | "danger" | "warning" | "info"
        title: "No Data",
        message: "No Employee data available to export",
        duration: 2500, // auto-close in 2.5s
      });
      return;
    }

    // Prepare data for Excel
    const excelData = filteredEmployees.map((emp, index) => ({
      "S.I": index + 1,
      "Employee ID": emp.employeeId || "N/A",
      "Full Name": emp.fullName || "N/A",
      Department: emp.department || "N/A",
      "Job Title": emp.jobTitle || "N/A",
      Mobile: emp.personalMobile || "N/A",
      Status: emp.employmentType || "N/A",
      Certifications: getCertificateCount(emp),
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employees");
    const columnWidths = [
      { wch: 5 },
      { wch: 15 },
      { wch: 25 },
      { wch: 20 },
      { wch: 25 },
      { wch: 15 },
      { wch: 15 },
      { wch: 20 },
    ];
    worksheet["!cols"] = columnWidths;

    XLSX.writeFile(workbook, "Employee_Details.xlsx");
  };

  const handleDownloadPDF = () => {
    const tableColumn = [
      "#",
      "Employee ID",
      "Full Name",
      "Department",
      "Job Title",
      "Mobile",
      "Status",
      "Certifications",
    ];
    const tableRows = currentItems.map((employee, index) => [
      indexOfFirstItem + index + 1,
      employee.employeeId || "N/A",
      employee.fullName || "N/A",
      employee.department || "N/A",
      employee.jobTitle || "N/A",
      employee.personalMobile || "N/A",
      employee.employmentType || "N/A",
      getCertificateCount(employee),
    ]);
    if (tableRows.length === 0) {
      showMyAlert({
        type: "warning", // "success" | "danger" | "warning" | "info"
        title: "No Data",
        message: "No data to download for the selected criteria",
        duration: 2500, // auto-close in 2.5s
      });
      return;
    }
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Employee Details", 14, 16);
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 22,
      styles: { fontSize: 10, cellPadding: 2 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { left: 14, right: 14 },
    });
    doc.save("Employee_Details.pdf");
  };

  return (
    <Container className="mt-3">
      {/* Header Section */}
      <div className="row align-items-center mb-2 border rounded bg-light py-2 mx-1">
        <div className="col-6 col-md-6">
          <h5 className="mb-0 d-flex align-items-start justify-content-center justify-content-md-start gap-2">
            Employee Details
          </h5>
        </div>

        <div className="col-6 col-md-6 d-flex justify-content-center justify-content-md-end gap-2 text-md-end">
          <Button
            variant="outline-success"
            size="sm"
            title="Download Excel"
            className="btn-mobile-xs"
            onClick={handleDownloadExcel}
          >
            Export Excel
          </Button>

          <Button
            variant="outline-danger"
            size="sm"
            title="Download PDF"
            className="btn-mobile-xs"
            onClick={handleDownloadPDF}
          >
            Export PDF
          </Button>
        </div>
      </div>

      {isMobile && (
        <div className="d-flex justify-content-end mb-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
        </div>
      )}

      <div className={`row align-items-center mb-3 border rounded bg-light py-2 mx-1 ${(showFilters ? "d-flex" : "d-none")}`}>
        <div className="col-12 col-md-4">
          <Form.Label className="fw-semibold small mb-1 ms-1">
            Search
          </Form.Label>
          <div className="input-group input-group-sm">
            <span className="input-group-text bg-light border-end-0">
              <Search size={16} className="text-muted" />
            </span>
            <input
              type="text"
              className="form-control border-start-0 border-end-0"
              placeholder="Search here..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="col-12 col-md-6 mt-2 mt-md-0">
          <Row className="g-2">
            {/* Start Date */}
            <div className="col-md-5 col-6">
              <Form.Label className="fw-semibold small mb-1 ms-1">
                Start Date
              </Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={startDateFilter}
                onChange={(e) => setStartDateFilter(e.target.value)}
                placeholder="Start Date"
              />
            </div>

            <div className="col-md-5 col-6">
              <Form.Label className="fw-semibold small mb-1 ms-1">
                End Date
              </Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={endDateFilter}
                onChange={(e) => setEndDateFilter(e.target.value)}
                placeholder="End Date"
                min={startDateFilter}
              />
            </div>

            {(startDateFilter || endDateFilter || searchTerm) && (
              <div className="col-md-2 col-12 d-flex align-items-end">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => {
                    setStartDateFilter("");
                    setEndDateFilter("");
                    setSearchTerm("");
                  }}
                  className="form-control"
                >
                  Clear
                </Button>
              </div>
            )}
          </Row>
        </div>
      </div>

      {/* Pagination Section */}
      {totalPages > 1 && (
        <div className="row align-items-center justify-content-between mb-2 p-2">
          {/* Items per page selector */}
          <div className="col-5 col-md-auto">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Paginate</span>
              <Dropdown>
                <Dropdown.Toggle
                  size="sm"
                  variant="outline-secondary"
                  id="dropdown-items-per-page"
                >
                  {itemsPerPage}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {[5, 10, 20, 50, 100].map((count) => (
                    <Dropdown.Item
                      key={count}
                      onClick={() => handleItemsPerPageChange(count)}
                      active={itemsPerPage === count}
                    >
                      {count}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          {/* Pagination controls */}
          <div className="col-7 col-md-auto">
            <Pagination
              size="sm"
              className="mb-0 justify-content-center justify-content-md-end"
            >
              <Pagination.First
                onClick={() => paginate(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              />

              {/* Mobile: Show simplified pagination */}
              {isMobile ? (
                <>
                  <Pagination.Item active>{currentPage}</Pagination.Item>
                  {totalPages > 1 && (
                    <>
                      <Pagination.Ellipsis disabled />
                      <Pagination.Item onClick={() => paginate(totalPages)}>
                        {totalPages}
                      </Pagination.Item>
                    </>
                  )}
                </>
              ) : (
                /* Desktop: Show smart pagination */
                (() => {
                  const items = [];
                  const maxVisiblePages = 5;
                  let startPage = Math.max(
                    1,
                    currentPage - Math.floor(maxVisiblePages / 2)
                  );
                  let endPage = Math.min(
                    totalPages,
                    startPage + maxVisiblePages - 1
                  );

                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  if (startPage > 1) {
                    items.push(<Pagination.Ellipsis key="start-ellipsis" />);
                  }

                  for (let number = startPage; number <= endPage; number++) {
                    items.push(
                      <Pagination.Item
                        key={number}
                        active={number === currentPage}
                        onClick={() => paginate(number)}
                      >
                        {number}
                      </Pagination.Item>
                    );
                  }

                  if (endPage < totalPages) {
                    items.push(<Pagination.Ellipsis key="end-ellipsis" />);
                  }

                  return items;
                })()
              )}

              <Pagination.Next
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => paginate(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        </div>
      )}

      {isMobile ? (
        <div>
          {currentItems.length > 0 ? (
            currentItems.map((employee, index) => (
              <div key={employee.id} className="mb-3">
                <div
                  className="card shadow-sm"
                  style={{ textAlign: "left", borderRadius: "12px" }}
                >
                  <div className="card-body table-responsive">
                    <table className="table table-striped table-bordered mb-2">
                      <tbody>
                        <tr>
                          <td className="fw-bold">SI</td>
                          <td>
                            {filteredEmployees.length -
                              indexOfFirstItem -
                              index}
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Employee ID</td>
                          <td>{displayValue(employee.employeeId)}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Full Name</td>
                          <td>{displayValue(employee.fullName)}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Department</td>
                          <td>{displayValue(employee.department)}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Job Title</td>
                          <td>{displayValue(employee.jobTitle)}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Mobile</td>
                          <td>{displayValue(employee.personalMobile)}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Approved At</td>
                          <td>{displayValue(employee.approvedDate)}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Status</td>
                          <td>{displayValue(employee.employmentType)}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Certifications</td>
                          <td>
                            {getCertificateCount(employee) > 0 ? (
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() =>
                                  handleViewCertificatesFromTable(employee)
                                }
                              >
                                View Certificates (
                                {getCertificateCount(employee)})
                              </Button>
                            ) : (
                              "No Certificates"
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleViewDetails(employee)}
                      >
                        View Details
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteClick(employee)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-4">No employees found</div>
          )}
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th id="text-white-important">S.I</th>
              <th id="text-white-important">Employee ID</th>
              <th id="text-white-important">Full Name</th>
              <th id="text-white-important">Department</th>
              <th id="text-white-important">Job Title</th>
              <th id="text-white-important">Mobile</th>
              <th id="text-white-important">Status</th>
              <th id="text-white-important">Approved At</th>
              <th id="text-white-important">Certifications</th>
              <th id="text-white-important">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.length > 0 ? (
              currentItems.map((employee, index) => (
                <tr key={employee.id}>
                  <td>{filteredEmployees.length - indexOfFirstItem - index}</td>
                  <td>{displayValue(employee.employeeId)}</td>
                  <td>{displayValue(employee.fullName)}</td>
                  <td>{displayValue(employee.department)}</td>
                  <td>{displayValue(employee.jobTitle)}</td>
                  <td>{displayValue(employee.personalMobile)}</td>
                  <td>{displayValue(employee.employmentType)}</td>
                  <td>{displayValue(employee.approvedDate)}</td>
                  <td>
                    {getCertificateCount(employee) > 0 ? (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() =>
                          handleViewCertificatesFromTable(employee)
                        }
                      >
                        View Certificates ({getCertificateCount(employee)})
                      </Button>
                    ) : (
                      "No Certificates"
                    )}
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleViewDetails(employee)}
                      >
                        View Details
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteClick(employee)}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="text-center">
                  No employees found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      {/* Showing entries info */}
      <div className="col-12 mb-2 text-end">
        <span className="text-muted">
          Showing {indexOfFirstItem + 1} to{" "}
          {Math.min(indexOfLastItem, filteredEmployees.length)} of{" "}
          {filteredEmployees.length} entries
        </span>
      </div>

      {selectedEmployee && (
        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          size="lg"
          centered
          scrollable
        >
          <Modal.Header closeButton className="text-black">
            <Modal.Title className="d-flex align-items-center">
              <i className="fas fa-user-circle me-2"></i>
              {displayValue(selectedEmployee.fullName)}'s Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            {/* Employee Header Section */}
            <div className="bg-light p-4 border-bottom">
              <Row className="align-items-center">
                <Col md={3} className="text-center">
                  <div className="mb-3 text-center">
                    {isEditing ? (
                      <>
                        <Image
                          src={
                            editingEmployee.photographPreview
                              ? editingEmployee.photographPreview
                              : selectedEmployee.photograph &&
                                selectedEmployee.photograph !== "null"
                                ? `https://api.dremerz.net/uploads/${selectedEmployee.photograph}`
                                : ""
                          }
                          rounded
                          className="border shadow-sm mb-2"
                          style={{
                            width: "150px",
                            height: "150px",
                            objectFit: "cover",
                          }}
                        />
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          size="sm"
                        />
                      </>
                    ) : selectedEmployee.photograph &&
                      selectedEmployee.photograph !== "null" ? (
                      <Image
                        src={`https://api.dremerz.net/uploads/${selectedEmployee.photograph}`}
                        rounded
                        className="border shadow-sm"
                        style={{
                          width: "150px",
                          height: "150px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <div
                        className="bg-secondary text-white d-flex align-items-center justify-content-center rounded"
                        style={{
                          width: "150px",
                          height: "150px",
                          fontSize: "48px",
                        }}
                      >
                        <i className="fas fa-user"></i>
                      </div>
                    )}
                    <div className="mt-2 small text-muted">Photograph</div>
                  </div>
                </Col>
                <Col md={9}>
                  <Row>
                    <Col md={6}>
                      {isEditing ? (
                        <Form.Group className="mb-3">
                          <Form.Label>Full Name</Form.Label>
                          <Form.Control
                            type="text"
                            name="fullName"
                            value={editingEmployee.fullName || ""}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      ) : (
                        <>
                          <h4 className="mb-1">
                            {displayValue(selectedEmployee.fullName)}
                          </h4>
                          <p className="text-muted mb-2">
                            {displayValue(selectedEmployee.jobTitle)}
                          </p>
                        </>
                      )}
                    </Col>
                    <Col md={6}>
                      <div className="mb-2">
                        <strong>Employee ID:</strong>
                        <span className="ms-2">
                          {displayValue(selectedEmployee.employeeId)}
                        </span>
                      </div>
                      {isEditing ? (
                        <Form.Group className="mb-3">
                          <Form.Label>Department</Form.Label>
                          <Form.Control
                            type="text"
                            name="department"
                            value={editingEmployee.department || ""}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      ) : (
                        <div className="mb-2">
                          <strong>Department:</strong>
                          <span className="ms-2">
                            {displayValue(selectedEmployee.department)}
                          </span>
                        </div>
                      )}
                      <div className="mb-2">
                        <strong>Date of Joining:</strong>
                        <span className="ms-2">
                          {displayValue(
                            getDateOfJoining(selectedEmployee.employeeId) ||
                            formatDate(selectedEmployee.dateOfJoining)
                          )}
                        </span>
                      </div>
                      <div className="mt-2">
                        {isEditing ? (
                          <>
                            <Image
                              src={
                                editingEmployee.signaturePreview
                                  ? editingEmployee.signaturePreview
                                  : selectedEmployee.signature &&
                                    selectedEmployee.signature !== "null"
                                    ? `https://api.dremerz.net/uploads/${selectedEmployee.signature}`
                                    : ""
                              }
                              className="border bg-white p-1 mb-2"
                              style={{
                                width: "120px",
                                height: "50px",
                                objectFit: "contain",
                              }}
                            />
                            <Form.Control
                              type="file"
                              accept="image/*"
                              onChange={handleSignatureUpload}
                              size="sm"
                            />
                          </>
                        ) : selectedEmployee.signature &&
                          selectedEmployee.signature !== "null" ? (
                          <Image
                            src={`https://api.dremerz.net/uploads/${selectedEmployee.signature}`}
                            className="border bg-white p-1"
                            style={{
                              width: "120px",
                              height: "50px",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <div
                            className="bg-light d-flex align-items-center justify-content-center rounded border"
                            style={{ width: "120px", height: "50px" }}
                          >
                            <span className="text-muted small">
                              No Signature
                            </span>
                          </div>
                        )}
                        <div className="small text-muted">Signature</div>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>

            {/* Details Sections */}
            <div className="p-4">
              <Row>
                {/* Personal Information */}
                <Col md={6} className="mb-4">
                  <Card className="h-100 shadow-sm">
                    <Card.Header className="text-dark">
                      <h6 className="mb-0">
                        <i className="fas fa-user me-2"></i>
                        Personal Information
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col sm={6}>
                          <div className="mb-3">
                            <small className="text-muted">Date of Birth</small>
                            {isEditing ? (
                              <Form.Control
                                type="date"
                                name="dateOfBirth"
                                value={
                                  editingEmployee.dateOfBirth === "null" ||
                                    !editingEmployee.dateOfBirth
                                    ? ""
                                    : editingEmployee.dateOfBirth
                                }
                                onChange={handleInputChange}
                              />
                            ) : (
                              <div>
                                {formatDate(selectedEmployee.dateOfBirth)}
                              </div>
                            )}
                          </div>
                          <div className="mb-3">
                            <small className="text-muted">Gender</small>
                            {isEditing ? (
                              <Form.Select
                                name="gender"
                                value={
                                  editingEmployee.gender === "null"
                                    ? ""
                                    : editingEmployee.gender || ""
                                }
                                onChange={handleInputChange}
                              >
                                <option value="">Select Gender</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                              </Form.Select>
                            ) : (
                              <div>{displayValue(selectedEmployee.gender)}</div>
                            )}
                          </div>
                          <div className="mb-3">
                            <small className="text-muted">Blood Group</small>
                            {isEditing ? (
                              <Form.Select
                                name="bloodGroup"
                                value={
                                  editingEmployee.bloodGroup === "null"
                                    ? ""
                                    : editingEmployee.bloodGroup || ""
                                }
                                onChange={handleInputChange}
                              >
                                <option value="">Select Blood Group</option>
                                <option value="A+">A+</option>
                                <option value="A1+">A1+</option>
                                <option value="A-">A-</option>
                                <option value="B+">B+</option>
                                <option value="B-">B-</option>
                                <option value="AB+">AB+</option>
                                <option value="AB-">AB-</option>
                                <option value="O+">O+</option>
                                <option value="O-">O-</option>
                                <option value="Bombay blood group (hh)">
                                  Bombay blood group (hh)
                                </option>
                              </Form.Select>
                            ) : (
                              <div>
                                {displayValue(selectedEmployee.bloodGroup)}
                              </div>
                            )}
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div className="mb-3">
                            <small className="text-muted">Marital Status</small>
                            {isEditing ? (
                              <Form.Select
                                name="maritalStatus"
                                value={
                                  editingEmployee.maritalStatus === "null"
                                    ? ""
                                    : editingEmployee.maritalStatus || ""
                                }
                                onChange={handleInputChange}
                              >
                                <option value="">Select Marital Status</option>
                                <option value="Single">Single</option>
                                <option value="Married">Married</option>
                                <option value="Divorced">Divorced</option>
                                <option value="Widowed">Widowed</option>
                              </Form.Select>
                            ) : (
                              <div>
                                {displayValue(selectedEmployee.maritalStatus)}
                              </div>
                            )}
                          </div>
                          <div className="mb-3">
                            <small className="text-muted">Nationality</small>
                            {isEditing ? (
                              <Form.Control
                                type="text"
                                name="nationality"
                                value={
                                  editingEmployee.nationality === "null"
                                    ? ""
                                    : editingEmployee.nationality || ""
                                }
                                onChange={handleInputChange}
                              />
                            ) : (
                              <div>
                                {displayValue(selectedEmployee.nationality)}
                              </div>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Contact Information */}
                <Col md={6} className="mb-4">
                  <Card className="h-100 shadow-sm">
                    <Card.Header className="text-dark">
                      <h6 className="mb-0">
                        <i className="fas fa-phone me-2"></i>
                        Contact Information
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-3">
                        <small className="text-muted">Mobile Number</small>
                        {isEditing ? (
                          <Form.Control
                            type="tel"
                            name="personalMobile"
                            value={
                              editingEmployee.personalMobile === "null"
                                ? ""
                                : editingEmployee.personalMobile || ""
                            }
                            onChange={handleInputChange}
                          />
                        ) : (
                          <div className="fw-bold">
                            {displayValue(selectedEmployee.personalMobile)}
                          </div>
                        )}
                      </div>
                      <div className="mb-3">
                        <small className="text-muted">Alternate Mobile</small>
                        {isEditing ? (
                          <Form.Control
                            type="tel"
                            name="alternateMobile"
                            value={
                              editingEmployee.alternateMobile === "null"
                                ? ""
                                : editingEmployee.alternateMobile || ""
                            }
                            onChange={handleInputChange}
                          />
                        ) : (
                          <div>
                            {displayValue(selectedEmployee.alternateMobile)}
                          </div>
                        )}
                      </div>
                      <div className="mb-3">
                        <small className="text-muted">Email Address</small>
                        {isEditing ? (
                          <Form.Control
                            type="email"
                            name="personalEmail"
                            value={
                              editingEmployee.personalEmail === "null"
                                ? ""
                                : editingEmployee.personalEmail || ""
                            }
                            onChange={handleInputChange}
                          />
                        ) : (
                          <div>
                            {displayValue(selectedEmployee.personalEmail)}
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Employment Details */}
                <Col md={6} className="mb-4">
                  <Card className="h-100 shadow-sm">
                    <Card.Header className="text-dark">
                      <h6 className="mb-0">
                        <i className="fas fa-briefcase me-2"></i>
                        Employment Details
                      </h6>
                    </Card.Header>

                    <Card.Body>
                      <Row>
                        {/* LEFT COLUMN */}
                        <Col sm={6}>
                          {/* Reporting Manager */}
                          <div className="mb-3">
                            <Form.Label className="text-muted mb-1">Reporting Manager</Form.Label>
                            {isEditing ? (
                              <Form.Control
                                type="text"
                                name="reportingManager"
                                value={
                                  editingEmployee.reportingManager === "null"
                                    ? ""
                                    : editingEmployee.reportingManager || ""
                                }
                                onChange={handleInputChange}
                              />
                            ) : (
                              <div>{displayValue(selectedEmployee.reportingManager)}</div>
                            )}
                          </div>

                          {/* Work Location */}
                          <div className="mb-3">
                            <Form.Label className="text-muted mb-1">Work Location</Form.Label>
                            {isEditing ? (
                              <Form.Control
                                type="text"
                                name="workLocation"
                                value={
                                  editingEmployee.workLocation === "null"
                                    ? ""
                                    : editingEmployee.workLocation || ""
                                }
                                onChange={handleInputChange}
                              />
                            ) : (
                              <div>{displayValue(selectedEmployee.workLocation)}</div>
                            )}
                          </div>
                        </Col>

                        {/* RIGHT COLUMN */}
                        <Col sm={6}>
                          {/* Reporting Manager ID */}
                          <div className="mb-3">
                            <Form.Label className="text-muted mb-1">Reporting Manager ID</Form.Label>
                            {isEditing ? (
                              <Form.Control
                                type="text"
                                name="reportingManagerEmpID"
                                value={
                                  editingEmployee.reportingManagerEmpID === "null"
                                    ? ""
                                    : editingEmployee.reportingManagerEmpID || ""
                                }
                                onChange={handleInputChange}
                              />
                            ) : (
                              <div>{displayValue(selectedEmployee.reportingManagerEmpID)}</div>
                            )}
                          </div>

                          {/* Probation Period */}
                          <div className="mb-3">
                            <Form.Label className="text-muted mb-1">Probation Period</Form.Label>
                            {isEditing ? (
                              <Form.Control
                                type="text"
                                name="probationPeriod"
                                value={
                                  editingEmployee.probationPeriod === "null"
                                    ? ""
                                    : editingEmployee.probationPeriod || ""
                                }
                                onChange={handleInputChange}
                              />
                            ) : (
                              <div>{displayValue(selectedEmployee.probationPeriod)}</div>
                            )}
                          </div>
                        </Col>

                        <hr className="mt-2" />

                        {/* PREVIOUS COMPANY DETAILS */}
                        <Col>
                          <div className="mb-3">
                            <Form.Label className="text-muted mb-1">Previous Company Details</Form.Label>
                            <br />
                            {isEditing ? (
                              <>
                                <small className="text-muted">
                                  Previous Company
                                </small>
                                <Form.Control
                                  type="text"
                                  name="previousCompany"
                                  value={
                                    editingEmployee.previousCompany === "null"
                                      ? ""
                                      : editingEmployee.previousCompany || ""
                                  }
                                  onChange={handleInputChange}
                                  placeholder="Previous Company"
                                  className="mb-2"
                                />

                                <small className="text-muted">
                                  Previous Designation
                                </small>
                                <Form.Control
                                  type="text"
                                  name="previousDesignation"
                                  value={
                                    editingEmployee.previousDesignation === "null"
                                      ? ""
                                      : editingEmployee.previousDesignation || ""
                                  }
                                  onChange={handleInputChange}
                                  placeholder="Previous Designation"
                                  className="mb-2"
                                />

                                <small className="text-muted">
                                  Previous Duration
                                </small>
                                <Form.Control
                                  type="text"
                                  name="previousDuration"
                                  value={
                                    editingEmployee.previousDuration === "null"
                                      ? ""
                                      : editingEmployee.previousDuration || ""
                                  }
                                  onChange={handleInputChange}
                                  placeholder="Employment Duration"
                                />
                              </>
                            ) : (
                              <>
                                <div><strong>Company:</strong> {displayValue(selectedEmployee.previousCompany)}</div>
                                <div><strong>Designation:</strong> {displayValue(selectedEmployee.previousDesignation)}</div>
                                <div><strong>Duration:</strong> {displayValue(selectedEmployee.previousDuration)}</div>
                              </>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>


                {/* Education Details */}
                <Col md={6} className="mb-4">
                  <Card className="h-100 shadow-sm">
                    <Card.Header className="text-dark">
                      <h6 className="mb-0">
                        <i className="fas fa-graduation-cap me-2"></i>
                        Education Details
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col sm={6}>
                          <div className="mb-3">
                            <small className="text-muted">
                              Highest Qualification
                            </small>
                            {isEditing ? (
                              <Form.Control
                                type="text"
                                name="highestQualification"
                                value={
                                  editingEmployee.highestQualification ===
                                    "null"
                                    ? ""
                                    : editingEmployee.highestQualification || ""
                                }
                                onChange={handleInputChange}
                              />
                            ) : (
                              <div className="fw-bold">
                                {displayValue(
                                  selectedEmployee.highestQualification
                                )}
                              </div>
                            )}
                          </div>
                          <div className="mb-3">
                            <small className="text-muted">University</small>
                            {isEditing ? (
                              <Form.Control
                                type="text"
                                name="university"
                                value={
                                  editingEmployee.university === "null"
                                    ? ""
                                    : editingEmployee.university || ""
                                }
                                onChange={handleInputChange}
                              />
                            ) : (
                              <div>
                                {displayValue(selectedEmployee.university)}
                              </div>
                            )}
                          </div>
                        </Col>

                        <Col sm={6}>
                          <div className="mb-3">
                            <small className="text-muted">
                              Year of Passing
                            </small>
                            {isEditing ? (
                              <Form.Control
                                type="text"
                                name="yearOfPassing"
                                value={
                                  editingEmployee.yearOfPassing === "null"
                                    ? ""
                                    : editingEmployee.yearOfPassing || ""
                                }
                                onChange={handleInputChange}
                              />
                            ) : (
                              <div>
                                {displayValue(selectedEmployee.yearOfPassing)}
                              </div>
                            )}
                          </div>
                          <div className="mb-3">
                            <small className="text-muted">Specialization</small>
                            {isEditing ? (
                              <Form.Control
                                type="text"
                                name="specialization"
                                value={
                                  editingEmployee.specialization === "null"
                                    ? ""
                                    : editingEmployee.specialization || ""
                                }
                                onChange={handleInputChange}
                              />
                            ) : (
                              <div>
                                {displayValue(selectedEmployee.specialization)}
                              </div>
                            )}
                          </div>
                        </Col>
                      </Row>

                      {/* Certificates Section - Single Button */}
                      <div className="mb-3">
                        <small className="text-muted">Certifications</small>
                        {isEditing ? (
                          <div>
                            {certificateFiles.map((cert, index) => (
                              <div
                                key={index}
                                className="mb-2 p-2 border rounded"
                              >
                                <Form.Group className="mb-2">
                                  <Form.Label className="small">
                                    Certificate Name
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    size="sm"
                                    placeholder="Enter certificate name"
                                    value={certificateNames[index] || ""}
                                    onChange={(e) =>
                                      handleCertificateNameChange(
                                        index,
                                        e.target.value
                                      )
                                    }
                                  />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                  <Form.Label className="small">
                                    Upload File
                                  </Form.Label>
                                  <Form.Control
                                    type="file"
                                    size="sm"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) =>
                                      handleCertificateFileChange(
                                        index,
                                        e.target.files[0]
                                      )
                                    }
                                  />
                                </Form.Group>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleRemoveCertificate(index)}
                                >
                                  Remove
                                </Button>
                              </div>
                            ))}

                            {certificateFiles.length < 5 && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={handleAddCertificate}
                                className="mt-2"
                              >
                                <i className="fas fa-plus me-1"></i>
                                Add Certificate ({certificateFiles.length}/5)
                              </Button>
                            )}
                          </div>
                        ) : (
                          <div className="mt-2">
                            {selectedEmployee.certifications &&
                              selectedEmployee.certifications !== "null" ? (
                              (() => {
                                try {
                                  const certs = JSON.parse(
                                    selectedEmployee.certifications
                                  );
                                  const certEntries = Array.isArray(certs)
                                    ? certs.map((cert, index) => [
                                      cert?.name ||
                                      `Certificate ${index + 1}`,
                                      cert?.fileName,
                                    ])
                                    : Object.entries(certs);

                                  if (certEntries.length === 0) {
                                    return (
                                      <span className="text-muted">
                                        No certificates available
                                      </span>
                                    );
                                  }

                                  return (
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() =>
                                        setShowCertificateListModal(true)
                                      }
                                    >
                                      View Certificates ({certEntries.length})
                                    </Button>
                                  );
                                } catch (e) {
                                  console.error(
                                    "Error parsing certificates:",
                                    e
                                  );
                                  return (
                                    <span className="text-muted">
                                      Invalid certificate data
                                    </span>
                                  );
                                }
                              })()
                            ) : (
                              <span className="text-muted">
                                No certificates available
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Bank Details */}
                <Col md={6} className="mb-4">
                  <Card className="h-100 shadow-sm">
                    <Card.Header className="text-dark">
                      <h6 className="mb-0">
                        <i className="fas fa-university me-2"></i>
                        Bank Details
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col sm={6}>
                          <div className="mb-3">
                            <small className="text-muted">Bank Name</small>
                            {isEditing ? (
                              <Form.Control
                                type="text"
                                name="bankName"
                                value={
                                  editingEmployee.bankName === "null"
                                    ? ""
                                    : editingEmployee.bankName || ""
                                }
                                onChange={handleInputChange}
                              />
                            ) : (
                              <div>
                                {displayValue(selectedEmployee.bankName)}
                              </div>
                            )}
                          </div>
                          <div className="mb-3">
                            <small className="text-muted">Account Holder</small>
                            {isEditing ? (
                              <Form.Control
                                type="text"
                                name="accountHolderName"
                                value={
                                  editingEmployee.accountHolderName === "null"
                                    ? ""
                                    : editingEmployee.accountHolderName || ""
                                }
                                onChange={handleInputChange}
                              />
                            ) : (
                              <div>
                                {displayValue(
                                  selectedEmployee.accountHolderName
                                )}
                              </div>
                            )}
                          </div>
                          <div className="mb-3">
                            <small className="text-muted">Account Number</small>
                            {isEditing ? (
                              <Form.Control
                                type="text"
                                name="accountNumber"
                                value={
                                  editingEmployee.accountNumber === "null"
                                    ? ""
                                    : editingEmployee.accountNumber || ""
                                }
                                onChange={handleInputChange}
                              />
                            ) : (
                              <div className="font-monospace">
                                {displayValue(selectedEmployee.accountNumber)}
                              </div>
                            )}
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div className="mb-3">
                            <small className="text-muted">IFSC Code</small>
                            {isEditing ? (
                              <Form.Control
                                type="text"
                                name="ifscCode"
                                value={
                                  editingEmployee.ifscCode === "null"
                                    ? ""
                                    : editingEmployee.ifscCode || ""
                                }
                                onChange={handleInputChange}
                              />
                            ) : (
                              <div className="font-monospace">
                                {displayValue(selectedEmployee.ifscCode)}
                              </div>
                            )}
                          </div>
                          <div className="mb-3">
                            <small className="text-muted">Branch Name</small>
                            {isEditing ? (
                              <Form.Control
                                type="text"
                                name="branchName"
                                value={
                                  editingEmployee.branchName === "null"
                                    ? ""
                                    : editingEmployee.branchName || ""
                                }
                                onChange={handleInputChange}
                              />
                            ) : (
                              <div>
                                {displayValue(selectedEmployee.branchName)}
                              </div>
                            )}
                          </div>
                          <div className="mb-3">
                            <small className="text-muted">PAN Number</small>
                            {isEditing ? (
                              <Form.Control
                                type="text"
                                name="panNumber"
                                value={
                                  editingEmployee.panNumber === "null"
                                    ? ""
                                    : editingEmployee.panNumber || ""
                                }
                                onChange={handleInputChange}
                              />
                            ) : (
                              <div className="font-monospace">
                                {displayValue(selectedEmployee.panNumber)}
                              </div>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Address Information */}
                <Col md={6} className="mb-4">
                  <Card className="h-100 shadow-sm">
                    <Card.Header className="text-dark">
                      <h6 className="mb-0">
                        <i className="fas fa-map-marker-alt me-2"></i>
                        Address Information
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-3">
                        <small className="text-muted">Current Address</small>
                        {isEditing ? (
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="currentAddress"
                            value={
                              editingEmployee.currentAddress === "null"
                                ? ""
                                : editingEmployee.currentAddress || ""
                            }
                            onChange={handleInputChange}
                          />
                        ) : (
                          <div>
                            {displayValue(selectedEmployee.currentAddress)}
                          </div>
                        )}
                      </div>
                      <div className="mb-3">
                        <small className="text-muted">Permanent Address</small>
                        {isEditing ? (
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="permanentAddress"
                            value={
                              editingEmployee.permanentAddress === "null"
                                ? ""
                                : editingEmployee.permanentAddress || ""
                            }
                            onChange={handleInputChange}
                          />
                        ) : (
                          <div>
                            {displayValue(selectedEmployee.permanentAddress)}
                          </div>
                        )}
                      </div>
                      <hr />
                      <div className="mb-3">
                        <small className="text-muted">Emergency Contact</small>
                        {isEditing ? (
                          <>
                            <Form.Control
                              type="text"
                              name="emergencyContactName"
                              value={
                                editingEmployee.emergencyContactName === "null"
                                  ? ""
                                  : editingEmployee.emergencyContactName || ""
                              }
                              onChange={handleInputChange}
                              placeholder="Name"
                              className="mb-2"
                            />
                            <Form.Control
                              type="text"
                              name="emergencyRelation"
                              value={
                                editingEmployee.emergencyRelation === "null"
                                  ? ""
                                  : editingEmployee.emergencyRelation || ""
                              }
                              onChange={handleInputChange}
                              placeholder="Relation"
                              className="mb-2"
                            />
                            <Form.Control
                              type="tel"
                              name="emergencyMobile"
                              value={
                                editingEmployee.emergencyMobile === "null"
                                  ? ""
                                  : editingEmployee.emergencyMobile || ""
                              }
                              onChange={handleInputChange}
                              placeholder="Mobile Number"
                            />
                          </>
                        ) : (
                          <>
                            <div>
                              <strong>
                                {displayValue(
                                  selectedEmployee.emergencyContactName
                                )}
                              </strong>
                              {selectedEmployee.emergencyRelation &&
                                selectedEmployee.emergencyRelation !==
                                "null" && (
                                  <span className="text-muted">
                                    {" "}
                                    (
                                    {displayValue(
                                      selectedEmployee.emergencyRelation
                                    )}
                                    )
                                  </span>
                                )}
                            </div>
                            {selectedEmployee.emergencyMobile &&
                              selectedEmployee.emergencyMobile !== "null" && (
                                <div className="fw-bold">
                                  {displayValue(
                                    selectedEmployee.emergencyMobile
                                  )}
                                </div>
                              )}
                          </>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          </Modal.Body>
          <Modal.Footer className="bg-light">
            {isEditing ? (
              <>
                <Button
                  variant="secondary"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSaveChanges}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                      <span className="ms-2">Saving...</span>
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Close
                </Button>
                <Button variant="primary" onClick={handleEditClick}>
                  Edit
                </Button>
              </>
            )}
          </Modal.Footer>
        </Modal>
      )}

      {/* Certificate List Modal */}
      <Modal
        show={showCertificateListModal}
        onHide={() => setShowCertificateListModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>Certificates - {selectedEmployee?.fullName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(() => {
            const certificates = parseCertificates(
              selectedEmployee?.certifications
            );

            if (certificates.length === 0) {
              return (
                <div className="text-center text-muted py-4">
                  <i className="fas fa-certificate fa-2x mb-3"></i>
                  <p>No certificates available</p>
                </div>
              );
            }

            return (
              <div className="certificate-list">
                {certificates.map((cert, index) => {
                  const certificateKey = `certificate_${index}`;
                  const certificateFileName =
                    selectedEmployee[certificateKey] || cert.fileName;

                  return (
                    <div
                      key={index}
                      className="border rounded p-3 mb-3 bg-light d-flex justify-content-between align-items-center"
                    >
                      <div className="flex-grow-1">
                        <div className="fw-medium mb-1">
                          {cert?.name || `Certificate ${index + 1}`}
                        </div>
                        {/* <small className="text-muted">
                          {certificateFileName
                            ? "File available"
                            : "File not available"}
                        </small> */}
                      </div>
                      {certificateFileName && certificateFileName !== "null" ? (
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              setShowCertificateListModal(false);
                              const fullUrl = `https://api.dremerz.net/uploads/${certificateFileName}`;
                              handleViewCertificate(
                                fullUrl,
                                cert?.name || `Certificate ${index + 1}`
                              );
                            }}
                          >
                            View
                          </Button>
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => {
                              const fullUrl = `https://api.dremerz.net/uploads/${certificateFileName}`;
                              handleDownloadCertificate(
                                fullUrl,
                                certificateFileName,
                                selectedEmployee.employeeId,
                                cert?.name || `Certificate ${index + 1}`
                              );
                            }}
                          >
                            Download
                          </Button>
                        </div>
                      ) : (
                        <Badge bg="secondary">File Not Available</Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCertificateListModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Certificate Modal with Enhanced Features */}
      <Modal
        show={showCertificateModal}
        onHide={() => {
          setShowCertificateModal(false);
          setZoomLevel(1);
          if (fromCertificateList) {
            setTimeout(() => {
              setShowCertificateListModal(true);
              setFromCertificateList(false);
            }, 100);
          }
        }}
        centered
        size="lg"
        contentClassName="border-0"
      >
        <Modal.Header
          closeButton
          className="d-flex justify-content-between align-items-center border-0 pb-2"
          style={{ padding: "1rem 1.5rem 0 1.5rem" }}
        >
          <Modal.Title style={{ fontSize: "1.25rem" }}>
            {currentCertificateName || "Certificate"}
          </Modal.Title>
          <div>
            <ButtonGroup size="sm" style={{ marginLeft: "15px" }}>
              <Button
                variant="outline-secondary"
                onClick={() =>
                  setZoomLevel((prev) => Math.max(0.5, prev - 0.1))
                }
                title="Zoom Out"
                style={{ padding: "0.25rem 0.5rem", borderColor: "#dee2e6" }}
              >
                <i className="fas fa-search-minus"></i>
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => setZoomLevel(1)}
                title="Reset Zoom"
                style={{
                  padding: "0.25rem 0.75rem",
                  borderColor: "#dee2e6",
                  width: "80px",
                }}
              >
                {Math.round(zoomLevel * 100)}%
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => setZoomLevel((prev) => Math.min(3, prev + 0.1))}
                title="Zoom In"
                style={{ padding: "0.25rem 0.5rem", borderColor: "#dee2e6" }}
              >
                <i className="fas fa-search-plus"></i>
              </Button>
            </ButtonGroup>
          </div>
        </Modal.Header>
        <Modal.Body
          className="p-0"
          style={{ height: "75vh", backgroundColor: "#f8f9fa" }}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              overflow: "auto",
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              padding: "20px",
              scrollbarWidth: "thin",
              scrollbarColor: "#888 #f1f1f1",
            }}
          >
            <div
              style={{
                transform: `scale(${zoomLevel})`,
                transformOrigin: "center top",
                transition: "transform 0.2s ease",
                position: "relative",
                width: "fit-content",
                height: "fit-content",
                maxWidth: "100%",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              {currentCertificate &&
                (currentCertificate.endsWith(".pdf") ? (
                  <iframe
                    src={`${currentCertificate}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0&scrollbar=0`}
                    style={{
                      width: "800px",
                      height: "600px",
                      border: "none",
                      maxWidth: "100%",
                      backgroundColor: "white",
                    }}
                    title="Certificate"
                  />
                ) : (
                  <img
                    src={currentCertificate}
                    alt="Certificate"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      display: "block",
                      backgroundColor: "white",
                    }}
                    onLoad={(e) => {
                      const img = e.target;
                      const container = img.parentElement.parentElement;
                      const scale = Math.min(
                        (container.clientWidth - 40) / img.naturalWidth,
                        (container.clientHeight - 40) / img.naturalHeight,
                        1
                      );
                      setZoomLevel(Math.max(scale, 0.8));
                    }}
                  />
                ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer
          className="border-0 pt-0"
          style={{ padding: "0 1.5rem 1rem 1.5rem" }}
        >
          <Button
            variant="secondary"
            onClick={() => {
              setShowCertificateModal(false);
              setZoomLevel(1);
              if (fromCertificateList) {
                setTimeout(() => {
                  setShowCertificateListModal(true);
                  setFromCertificateList(false);
                }, 100);
              }
            }}
            style={{ padding: "0.375rem 0.75rem" }}
          >
            Back to List
          </Button>
          <Button
            variant="success"
            onClick={() => {
              const filename =
                currentCertificate.split("/").pop() || "certificate.pdf";
              handleDownloadCertificate(
                currentCertificate,
                filename,
                selectedEmployee.employeeId,
                currentCertificateName
              );
            }}
            style={{ padding: "0.375rem 0.75rem" }}
          >
            Download
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-exclamation-triangle me-2"></i>
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-3">
              <i className="fas fa-trash-alt fa-3x text-danger mb-3"></i>
              <h5>Are you sure you want to delete this employee?</h5>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span className="ms-2">Deleting...</span>
              </>
            ) : (
              <>
                Delete
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default EmployeeDetail;
